import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/Graphs/Dropdown";
import { Input } from "components/Graphs/Input";
import CopyField from "components/UI-lib/CopyField";

const INSTALL_BREV_CLI_COMMAND =
  'bash -c "$(curl -fsSL https://raw.githubusercontent.com/brevdev/brev-cli/main/bin/install-latest.sh)"';

type LinuxUserSetupMode = "existing" | "new";

function validateLinuxUsername(name: string): string {
  if (name.length > 32) return "Username must be 32 characters or fewer.";
  if (!/^[a-z_][a-z0-9_-]*$/.test(name)) {
    return "Must start with a lowercase letter or underscore, and contain only lowercase letters, numbers, hyphens, and underscores.";
  }
  return "";
}

export const LinuxUserSetupInstructions: React.FC<{
  idPrefix: string;
  className?: string;
  variant?: "card" | "inline";
  summaryLabel?: string;
}> = ({
  idPrefix,
  className,
  variant = "card",
  summaryLabel = "Share SSH access to a different Linux user",
}) => {
  const [linuxUserMode, setLinuxUserMode] =
    useState<LinuxUserSetupMode>("existing");
  const [linuxUser, setLinuxUser] = useState("brev-user");
  const [userError, setUserError] = useState("");
  const isInline = variant === "inline";
  const effectiveUser =
    linuxUser.trim() && !userError ? linuxUser.trim() : "<linux-user>";

  const handleLinuxUserChange = (value: string) => {
    setLinuxUser(value);
    setUserError(value ? validateLinuxUsername(value) : "");
  };

  return (
    <details
      className={
        isInline
          ? `group ${className ?? ""}`
          : `group overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900/30 ${className ?? ""}`
      }
    >
      <summary
        className={
          isInline
            ? "inline-flex cursor-pointer list-none items-center gap-1.5 py-1 text-sm text-secondary [&::-webkit-details-marker]:hidden"
            : "flex cursor-pointer list-none items-center justify-between px-4 py-3"
        }
      >
        <span
          className={isInline ? undefined : "text-sm font-medium text-primary"}
        >
          {summaryLabel}
        </span>
        <ChevronDown
          aria-hidden="true"
          className="h-4 w-4 shrink-0 text-secondary transition-transform group-open:rotate-180"
        />
      </summary>
      <div
        className={
          isInline
            ? "mt-2 space-y-4 rounded-md border border-zinc-800/80 p-4"
            : "space-y-4 border-t border-zinc-800 px-4 py-4"
        }
      >
        <div className="space-y-2">
          <label
            htmlFor={`${idPrefix}-linux-user`}
            className="text-sm font-medium text-white"
          >
            Linux user
          </label>
          <p className="text-sm leading-[1.6] text-secondary">
            SSH access must be enabled separately for each Linux user.
          </p>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Linux user type"
                  className="inline-flex h-9 shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-zinc-800 bg-zinc-900 px-2 text-sm text-zinc-300 outline-none transition-colors hover:border-highlight/60 focus-visible:border-highlight/60"
                >
                  {linuxUserMode === "existing"
                    ? "An existing Linux user"
                    : "A new Linux user"}
                  <ChevronDown
                    aria-hidden="true"
                    className="h-3.5 w-3.5 shrink-0 text-zinc-500"
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onSelect={() => setLinuxUserMode("existing")}>
                  An existing Linux user
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setLinuxUserMode("new")}>
                  A new Linux user
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Input
              id={`${idPrefix}-linux-user`}
              value={linuxUser}
              onChange={(event) => handleLinuxUserChange(event.target.value)}
              placeholder="linux-user"
              aria-invalid={Boolean(userError)}
              hasError={Boolean(userError)}
              inputClassName="text-sm"
              className="min-w-0 max-w-sm flex-1"
            />
          </div>
          {userError ? (
            <p className="text-sm text-red-400" role="alert">
              {userError}
            </p>
          ) : null}
          {linuxUserMode === "existing" ? (
            <CopyField
              value={`sudo su - ${effectiveUser}`}
              analytics={{ userid: "", eventLabel: "switch linux user" }}
              className="w-full"
            />
          ) : (
            <div className="space-y-2">
              <p className="text-sm font-medium text-white">
                Create the user, switch to it, and install the Brev CLI
              </p>
              <CopyField
                value={`sudo adduser ${effectiveUser}`}
                analytics={{ userid: "", eventLabel: "add linux user" }}
                className="w-full"
              />
              <CopyField
                value={`sudo su - ${effectiveUser}`}
                analytics={{ userid: "", eventLabel: "switch linux user" }}
                className="w-full"
              />
              <CopyField
                value={INSTALL_BREV_CLI_COMMAND}
                analytics={{ userid: "", eventLabel: "install brev cli" }}
                className="w-full"
              />
              <CopyField
                value={`export PATH="/home/${effectiveUser}/.local/bin:$PATH"`}
                analytics={{ userid: "", eventLabel: "update path" }}
                className="w-full"
              />
            </div>
          )}
        </div>
      </div>
    </details>
  );
};
